import { useState } from 'react';

import { cn } from '../../styles/lib/utils';
import { Button } from '../Button';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { SvgIcon } from '../SvgIcon';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { SubLayerTypeIcon } from './assets/subLayerTypeIcons';
import type { LayerInfo } from './types';

/** "Preview (CG)" glyph from the design — the popover trigger. */
function PreviewGlyph({ className }: { className?: string }) {
  return (
    <svg
      width="12"
      height="13"
      viewBox="0 0 11.2334 12.1824"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M2.94629 5.45996C4.29364 5.59714 5.3456 6.73561 5.3457 8.11914L5.33203 8.39258C5.286 8.84517 5.12418 9.26291 4.88086 9.62012L6.58984 11.3291C6.78463 11.5243 6.78478 11.841 6.58984 12.0361C6.39472 12.2313 6.07811 12.231 5.88281 12.0361L4.17285 10.3262C3.7447 10.6178 3.22981 10.7919 2.67285 10.792L2.39941 10.7783C1.14171 10.6506 0.14164 9.65022 0.0136719 8.39258L0 8.11914C0.000105663 6.64312 1.19681 5.44629 2.67285 5.44629L2.94629 5.45996ZM10.2334 0C10.7855 0.000246298 11.2334 0.447868 11.2334 1V9.33301C11.2334 9.88511 10.7855 10.3328 10.2334 10.333H7L6.89941 10.3232C6.6715 10.2767 6.50003 10.0747 6.5 9.83301C6.5 9.59132 6.67148 9.38932 6.89941 9.34277L7 9.33301H10.2334V1H1.90039V4.2334C1.90015 4.50907 1.67602 4.73298 1.40039 4.7334C1.1244 4.7334 0.900637 4.50933 0.900391 4.2334V1C0.900391 0.447715 1.34811 0 1.90039 0H10.2334ZM2.67285 6.44629C1.7491 6.44629 1.00011 7.19541 1 8.11914C1.00018 9.04281 1.74914 9.79199 2.67285 9.79199C3.59633 9.79171 4.34552 9.04264 4.3457 8.11914C4.3456 7.19558 3.59637 6.44657 2.67285 6.44629Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Format a timestamp as e.g. "January 1, 1970 12:00 A.M." */
function formatInfoDateTime(date: Date): string {
  const day = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const time = date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace('AM', 'A.M.')
    .replace('PM', 'P.M.');
  return `${day} ${time}`;
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-5">
      <Text className="text-grey-60 w-[76px] shrink-0 text-xs">{label}</Text>
      <Text className="text-primary text-xs font-medium">{value}</Text>
    </div>
  );
}

export type LayerInfoPopoverProps = {
  /** The layer detail to show. */
  layer: LayerInfo;
  /** Controlled open state. When omitted, the popover manages its own state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Side of the trigger the popover opens on. Defaults to `right` — the content is too
   * tall for above/below placement on short screens, while a horizontal side centers on
   * the button and Radix can shift it vertically (and flip right↔left) to stay on screen.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  /** Trigger-button test id. Callers rendering one popover per row suffix it with the row id. */
  'data-testid'?: string;
};

/** Info button + popover showing extended layer detail, anchored to the Name-column button. */
function LayerInfoPopover({
  layer,
  open: openProp,
  onOpenChange,
  side = 'right',
  className,
  'data-testid': dataTestId,
}: LayerInfoPopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const handleOpenChange = (next: boolean) => {
    // Only own the open state when uncontrolled: if a parent passes `open`, writing
    // `internalOpen` here would fight the controlled value on the next render.
    if (openProp === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip text="Click to preview this layer." asChild>
        <PopoverTrigger asChild>
          {/* Hidden at rest; revealed by the Table's LOGICAL row hover/focus state
              (`data-hovered` on the GridRow, synced across pinned sections and set by
              keyboard focus too). Opacity (not visibility) hiding keeps the button
              keyboard-reachable — its own focus highlights the row, which reveals it.
              Open popover keeps its trigger shown as blue "selected". */}
          <Button
            variant={open ? 'selected' : 'hollow'}
            size="icon"
            className={cn(
              'size-6 shrink-0 transition-colors',
              !open && 'text-grey-50 opacity-0 group-data-[hovered=true]/row:opacity-100',
              className,
            )}
            aria-label="View layer details"
            data-testid={dataTestId ?? 'layer-name-info'}
          >
            <PreviewGlyph />
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        side={side}
        // Beside the button the popover centers on it vertically; above/below keeps the
        // design's start align.
        align={side === 'left' || side === 'right' ? 'center' : 'start'}
        className="w-[340px] p-3"
        data-testid="layer-info-popover"
      >
        <div className="flex flex-col gap-4">
          <Text className="text-primary text-base font-semibold">{layer.name}</Text>

          {/* Live map preview — placeholder when the consumer supplies none. */}
          <div
            className="border-grey-40 bg-grey-20 flex h-[132px] items-center justify-center overflow-hidden rounded-lg border"
            data-testid="layer-info-preview"
          >
            {layer.preview ?? (
              <div className="text-grey-50 flex flex-col items-center gap-1.5">
                <SvgIcon icon="foldingMap" size={28} />
                {layer.previewPlaceholder === 'imaging' && (
                  <Text className="text-grey-50 text-xs" data-testid="layer-info-preview-imaging">
                    Imaging layer
                  </Text>
                )}
              </div>
            )}
          </div>

          {layer.description && <Text className="text-primary text-xs leading-normal">{layer.description}</Text>}

          <div className="flex flex-col gap-3">
            {layer.source && <InfoField label="Source" value={layer.source} />}
            {layer.lastUpdated && <InfoField label="Last Updated" value={formatInfoDateTime(layer.lastUpdated)} />}
            {layer.dateCreated && <InfoField label="Date Created" value={formatInfoDateTime(layer.dateCreated)} />}
          </div>

          {/* Included Sub-Layers — only for parent/grouped layers that have them. */}
          {layer.subLayers && layer.subLayers.length > 0 && (
            <div className="flex flex-col gap-3" data-testid="layer-info-sublayers">
              <div className="flex items-center gap-2">
                <Text className="text-primary text-sm font-semibold">Included Sub-Layers</Text>
                <span className="border-grey-30 text-primary-dark rounded-full border px-1.5 text-2xs leading-tight">
                  {layer.subLayers.length}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {layer.subLayers.map((subLayer) => (
                  <div key={subLayer.name} className="flex min-w-0 items-start gap-2">
                    {/* mt-px centers the 14px icon on the name's 16px first line. */}
                    <SubLayerTypeIcon type={subLayer.type} size={14} className="text-grey-60 mt-px shrink-0" />
                    <Text className="text-primary break-words text-xs">{subLayer.name}</Text>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { LayerInfoPopover };
